import type { HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { joinCss } from '@/shared/lib/css-utils';
import { Heading } from '../heading';
import css from './modal.module.scss';

type ModalProps = HTMLAttributes<HTMLDivElement> & {
  open: boolean;
  onClose: () => void;
};

export const Modal = ({
  open,
  onClose,
  className,
  children,
  ...restProps
}: ModalProps) => {
  if (!open) return null;

  // Portal to document.body so the overlay escapes any ancestor
  // stacking context (planner day rows + sortable wrappers each
  // create one). Also avoids ancestor transforms shifting the
  // `position: fixed` overlay off the viewport.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={css.overlay}>
      <button
        aria-label="Close modal"
        className={css.backdrop}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        tabIndex={-1}
        type="button"
      />
      <div className={joinCss(css.content, className)} {...restProps}>
        {children}
      </div>
    </div>,
    document.body
  );
};

type ModalHeaderProps = HTMLAttributes<HTMLDivElement> & {
  title: string;
  onClose?: () => void;
};

export const ModalHeader = ({
  title,
  onClose,
  className,
  ...restProps
}: ModalHeaderProps) => (
  <div className={joinCss(css.header, className)} {...restProps}>
    <Heading as="h2" size="xl">
      {title}
    </Heading>
    {onClose && (
      <button
        aria-label="Close"
        className={css.closeBtn}
        onClick={onClose}
        type="button"
      >
        ✕
      </button>
    )}
  </div>
);

type ModalFooterProps = HTMLAttributes<HTMLDivElement>;

export const ModalFooter = ({
  className,
  children,
  ...restProps
}: ModalFooterProps) => (
  <div className={joinCss(css.footer, className)} {...restProps}>
    {children}
  </div>
);
